<template>
  <div v-if="currentClaim">
    <div class="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
      <ShowPageHeader backLabel="Sinistres" :backRoute="backRoute" :title="currentClaim.friendlyId">
        <template #badges>
          <FieldSpan
            :class="[statusStyle(currentClaim.status)]"
            translation-prefix="claim.status"
            :value="currentClaim.status"
          />
          <div
            v-if="claimPickupScheduled && currentClaim.pickupAppointmentDate"
            class="flex items-center gap-1.5 rounded-lg p-2 text-xs text-gray-600 bg-blue-100"
          >
            <CalendarIcon class="w-4 h-4 text-blue-500" />
            <span>RDV de reprise le {{ currentClaim.pickupAppointmentDate }}</span>
          </div>
        </template>
      </ShowPageHeader>

      <div v-if="anyRequiredAction">
        <RequiredAction :claim="currentClaim" />
      </div>
    </div>

    <Alert v-if="handlerNoticeMessage" type="info" class="mt-4">
      <template #title>{{ t("handler_notice_title") }}&nbsp;</template>
      {{ handlerNoticeMessage }}
    </Alert>

    <CompensationValueAlert
      v-if="replacementHandlingType && currentClaim.compensationValue"
      :compensation-value="currentClaim.compensationValue"
      class="mt-4"
    />

    <div class="mt-4 flex flex-col lg:flex-row gap-3">
      <div class="flex-1 min-w-0">
        <div class="relative bg-white rounded-2xl shadow-md p-6">
          <h3 class="text-lg font-bold text-gray-900">Déclaration</h3>
          <ClaimDeclaration
            v-if="currentClaim.declarations && currentClaim.declarations.length"
            class="mt-4"
            :declaration="currentClaim.declarations[0]"
          />

          <ClaimProductSummary v-if="claimProduct" :claim-product="claimProduct" class="mt-4" />
        </div>
      </div>

      <ClaimDetailsSidebar :claim="currentClaim" :replacementHandlingType="replacementHandlingType" />
    </div>

    <ClaimDetailsTabContent :claim="currentClaim" />
  </div>

  <ClaimNotFound v-else />
</template>

<script>
import ClaimNotFound from "@/components/claim/details/utils/ClaimNotFound.vue";
import ClaimDetailsSidebar from "@/components/claim/details/ClaimDetailsSidebar.vue";
import ClaimDetailsTabContent from "@/components/claim/details/ClaimDetailsTabContent.vue";
import RequiredAction from "@/components/claim/details/RequiredAction.vue";
import { CalendarIcon } from "@heroicons/vue/outline";
import { mapActions, mapGetters, mapState } from "vuex";
import { useI18n } from "vue-i18n";
import {
  Alert,
  ClaimDeclaration,
  ClaimProductSummary,
  FieldSpan,
  CompensationValueAlert,
  ShowPageHeader,
} from "@estaly/ui";

export default {
  components: {
    Alert,
    CalendarIcon,
    CompensationValueAlert,
    ClaimNotFound,
    ClaimDeclaration,
    ClaimProductSummary,
    ClaimDetailsSidebar,
    ClaimDetailsTabContent,
    RequiredAction,
    FieldSpan,
    ShowPageHeader,
  },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  beforeMount() {
    this.fetch(this.$route.params.id);
  },

  computed: {
    ...mapGetters("claims", [
      "anyRequiredAction",
      "claimPickupScheduled",
      "statusStyle",
      "replacementHandlingType",
      "customerQuotationExpected",
      "customerInvoiceExpected",
    ]),
    ...mapState("claims", ["currentClaim"]),

    claimProduct() {
      return this.currentClaim?.claimProduct;
    },

    handlerNoticeMessage() {
      if (this.customerQuotationExpected) return this.t("handler_notice_quotation");
      if (this.customerInvoiceExpected) return this.t("handler_notice_invoice");
      return null;
    },

    backRoute() {
      const path = this.$route.path;
      if (path.includes("/platform/")) return "/platform/claims";
      if (path.includes("/pos/")) return "/pos/claims";
      return "/admin/claims";
    },
  },

  methods: {
    ...mapActions("claims", ["fetch", "reset"]),
  },
};
</script>

<i18n src="./ClaimDetails.i18n.json"></i18n>
